<template>
  <div class="card richiesta-ferie-page">
    <div class="page-header">
      <div>
        <h3 class="m-0">Ferie e permessi</h3>
        <p class="page-subtitle">
          {{ isApprover ? 'Gestisci le richieste di tutti i dipendenti' : 'Invia e consulta le tue richieste di ferie e permessi' }}
        </p>
      </div>
      <Button
        icon="pi pi-plus"
        label="Nuova richiesta"
        @click="openNewDialog"
      />
    </div>

    <Loader v-if="loading" />

    <template v-else>
      <div v-if="isApprover" class="filters-row">
        <span class="filter-label">Filtra stato:</span>
        <div class="filter-chips">
          <button
            v-for="opt in statoFilterOptions"
            :key="opt.value || 'all'"
            type="button"
            class="filter-chip"
            :class="{ active: statoFilter === opt.value }"
            @click="setStatoFilter(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
        <Button
          icon="pi pi-file-excel"
          :label="`Esporta ${statoFilterLabel}`"
          class="p-button-outlined export-btn"
          :disabled="!richieste.length"
          @click="exportExcel"
        />
      </div>

      <DataTable
        :value="richieste"
        paginator
        :rows="10"
        :rowsPerPageOptions="[10, 20, 50]"
        showGridlines
        responsiveLayout="scroll"
        emptyMessage="Nessuna richiesta"
        class="ferie-table"
      >
        <Column v-if="isApprover" field="richiedente" header="Dipendente" style="min-width: 160px">
          <template #body="{ data }">
            <div class="user-cell">
              <strong>{{ data.richiedente || '—' }}</strong>
              <small v-if="data.richiedente_email">{{ data.richiedente_email }}</small>
            </div>
          </template>
        </Column>
        <Column field="tipo" header="Tipo" style="min-width: 110px">
          <template #body="{ data }">
            <Tag :value="tipoLabel(data.tipo)" :severity="tipoSeverity(data.tipo)" />
          </template>
        </Column>
        <Column header="Periodo" style="min-width: 180px">
          <template #body="{ data }">
            <div class="periodo-cell">
              <strong>{{ formatPeriodo(data) }}</strong>
              <small v-if="data.tipo === 'ore'">{{ formatOreFascia(data) }}</small>
            </div>
          </template>
        </Column>
        <Column field="motivazione" header="Motivazione" style="min-width: 220px">
          <template #body="{ data }">
            <span class="motivazione-text">{{ data.motivazione }}</span>
          </template>
        </Column>
        <Column field="stato" header="Stato" style="min-width: 120px">
          <template #body="{ data }">
            <Tag :value="statoLabel(data.stato)" :severity="statoSeverity(data.stato)" />
          </template>
        </Column>
        <Column field="created_at" header="Inviata il" style="min-width: 130px">
          <template #body="{ data }">{{ formatDateTime(data.created_at) }}</template>
        </Column>
        <Column v-if="isApprover" header="Gestione" style="min-width: 200px">
          <template #body="{ data }">
            <div v-if="data.stato === 'in_attesa'" class="azioni">
              <Button
                icon="pi pi-check"
                class="p-button-success p-button-sm"
                label="Accetta"
                :loading="gestendoId === data.id"
                @click="openGestisci(data, 'accettata')"
              />
              <Button
                icon="pi pi-times"
                class="p-button-danger p-button-sm"
                label="Respingi"
                :loading="gestendoId === data.id"
                @click="openGestisci(data, 'respinta')"
              />
              <Button
                v-if="canRevoke(data)"
                icon="pi pi-undo"
                class="p-button-secondary p-button-sm"
                label="Revoca"
                :loading="revocandoId === data.id"
                @click="confirmRevoca(data)"
              />
            </div>
            <div v-else class="gestione-info">
              <small v-if="data.gestito_da_nome">da {{ data.gestito_da_nome }}</small>
              <small v-if="data.gestito_il">{{ formatDateTime(data.gestito_il) }}</small>
              <small v-if="data.note_gestione" class="note-gestione">{{ data.note_gestione }}</small>
              <Button
                v-if="canRevoke(data)"
                icon="pi pi-undo"
                class="p-button-secondary p-button-sm mt-2"
                label="Revoca"
                :loading="revocandoId === data.id"
                @click="confirmRevoca(data)"
              />
            </div>
          </template>
        </Column>
        <Column v-else header="Azioni" style="min-width: 180px">
          <template #body="{ data }">
            <div class="azioni-employee">
              <Button
                v-if="canRevoke(data)"
                icon="pi pi-undo"
                class="p-button-secondary p-button-sm"
                label="Revoca"
                :loading="revocandoId === data.id"
                @click="confirmRevoca(data)"
              />
              <span v-if="data.note_gestione" class="note-gestione">{{ data.note_gestione }}</span>
              <span v-else-if="!canRevoke(data)" class="text-muted">—</span>
            </div>
          </template>
        </Column>
      </DataTable>
    </template>

    <Dialog
      v-model:visible="showCreateDialog"
      header="Nuova richiesta"
      modal
      :style="{ width: '520px' }"
      :breakpoints="{ '640px': '95vw' }"
    >
      <div class="field field-full tipo-field">
        <label>Tipo *</label>
        <div class="filter-chips">
          <button
            v-for="opt in tipoOptions"
            :key="opt.value"
            type="button"
            class="filter-chip"
            :class="{ active: form.tipo === opt.value }"
            @click="setFormTipo(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <div class="form-grid">
        <template v-if="form.tipo === 'giorni'">
          <div class="field">
            <label for="dataInizio">Dal *</label>
            <Calendar
              id="dataInizio"
              v-model="form.data_inizio"
              dateFormat="dd/mm/yy"
              :showIcon="true"
              :manualInput="false"
              class="w-full"
            />
          </div>
          <div class="field">
            <label for="dataFine">Al *</label>
            <Calendar
              id="dataFine"
              v-model="form.data_fine"
              dateFormat="dd/mm/yy"
              :showIcon="true"
              :manualInput="false"
              :minDate="form.data_inizio || null"
              class="w-full"
            />
          </div>
        </template>

        <template v-else>
          <div class="field">
            <label for="dataOre">Data *</label>
            <Calendar
              id="dataOre"
              v-model="form.data_inizio"
              dateFormat="dd/mm/yy"
              :showIcon="true"
              :manualInput="false"
              class="w-full"
            />
          </div>
          <div class="field">
            <label for="ore">Ore *</label>
            <InputNumber
              id="ore"
              v-model="form.ore"
              :min="0.5"
              :max="8"
              :minFractionDigits="0"
              :maxFractionDigits="1"
              :step="0.5"
              suffix=" ore"
              :showButtons="true"
              class="w-full"
            />
          </div>
          <div class="field">
            <label for="oraInizio">Dalle</label>
            <Calendar
              id="oraInizio"
              v-model="form.ora_inizio"
              timeOnly
              hourFormat="24"
              :manualInput="false"
              class="w-full"
              @update:modelValue="onOreTimesChange"
            />
          </div>
          <div class="field">
            <label for="oraFine">Alle</label>
            <Calendar
              id="oraFine"
              v-model="form.ora_fine"
              timeOnly
              hourFormat="24"
              :manualInput="false"
              class="w-full"
              @update:modelValue="onOreTimesChange"
            />
          </div>
        </template>

        <div class="field field-full">
          <label for="motivazione">Motivazione *</label>
          <Textarea
            id="motivazione"
            v-model="form.motivazione"
            rows="4"
            class="w-full"
            maxlength="2000"
            autoResize
          />
        </div>
      </div>
      <template #footer>
        <Button label="Annulla" class="p-button-text" @click="showCreateDialog = false" />
        <Button label="Invia richiesta" icon="pi pi-send" :loading="saving" @click="submitCreate" />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="showGestisciDialog"
      :header="gestisciStato === 'accettata' ? 'Accetta richiesta' : 'Respingi richiesta'"
      modal
      :style="{ width: '420px' }"
      :breakpoints="{ '640px': '95vw' }"
    >
      <p v-if="selectedRichiesta" class="gestisci-summary">
        {{ selectedRichiesta.richiedente }} —
        {{ tipoLabel(selectedRichiesta.tipo) }} —
        {{ formatPeriodo(selectedRichiesta) }}
        <template v-if="selectedRichiesta.tipo === 'ore'">
          · {{ formatOreFascia(selectedRichiesta) }}
        </template>
      </p>
      <div class="field">
        <label for="noteGestione">Nota (opzionale)</label>
        <Textarea id="noteGestione" v-model="noteGestione" rows="3" class="w-full" maxlength="2000" />
      </div>
      <template #footer>
        <Button label="Annulla" class="p-button-text" @click="showGestisciDialog = false" />
        <Button
          :label="gestisciStato === 'accettata' ? 'Conferma accettazione' : 'Conferma rifiuto'"
          :class="gestisciStato === 'accettata' ? 'p-button-success' : 'p-button-danger'"
          :loading="gestendoId != null"
          @click="confirmGestisci"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { computed, getCurrentInstance, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import * as XLSX from 'xlsx'
import PanelAdminService from '@/services/ask-admin/panel-admin'
import Loader from '@/components/Loader.vue'

const emptyForm = () => ({
  tipo: 'giorni',
  data_inizio: null,
  data_fine: null,
  ore: null,
  ora_inizio: null,
  ora_fine: null,
  motivazione: '',
})

export default {
  name: 'RichiestaFerie',
  components: { Loader },
  setup() {
    const router = useRouter()
    const toast = useToast()
    const confirm = useConfirm()
    const self = getCurrentInstance()
    self.$eventBus = self.appContext.config.globalProperties.$eventBus
    self.$http = self.appContext.config.globalProperties.$http

    const service = new PanelAdminService(self.$http, self.$eventBus)


    
    const loading = ref(true)
    const saving = ref(false)
    const richieste = ref([])
    const isApprover = ref(false)
    const statoFilter = ref(null)
    const showCreateDialog = ref(false)
    const showGestisciDialog = ref(false)
    const selectedRichiesta = ref(null)
    const gestisciStato = ref('accettata')
    const noteGestione = ref('')
    const gestendoId = ref(null)
    const revocandoId = ref(null)

    const getCurrentUserId = () => {
      try {
        const stored = localStorage.getItem('user')
        if (!stored) return null
        return Number(JSON.parse(atob(stored)).user[0]?.id) || null
      } catch (e) {
        return null
      }
    }

    const currentUserId = getCurrentUserId()

    const form = ref(emptyForm())

    const statoFilterOptions = [
      { label: 'Tutte', value: null },
      { label: 'In attesa', value: 'in_attesa' },
      { label: 'Accettate', value: 'accettata' },
      { label: 'Respinte', value: 'respinta' },
      { label: 'Revocate', value: 'revocata' },
    ]

    const tipoOptions = [
      { label: 'Giorni di ferie', value: 'giorni' },
      { label: 'Ore / permesso', value: 'ore' },
    ]

    const statoFilterLabel = computed(() => {
      const opt = statoFilterOptions.find((item) => item.value === statoFilter.value)
      return opt?.label || 'Tutte'
    })

    const formatDate = (value) => {
      if (!value) return '—'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return value
      return d.toLocaleDateString('it-IT')
    }

    const formatDateTime = (value) => {
      if (!value) return '—'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return value
      return d.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const formatTime = (value) => {
      if (!value) return ''
      if (value instanceof Date) {
        const h = String(value.getHours()).padStart(2, '0')
        const m = String(value.getMinutes()).padStart(2, '0')
        return `${h}:${m}`
      }
      return String(value).slice(0, 5)
    }

    const formatOre = (value) => {
      const n = Number(value)
      if (!Number.isFinite(n) || n <= 0) return ''
      const label = String(n).replace('.', ',')
      return n === 1 ? '1 ora' : `${label} ore`
    }

    const formatPeriodo = (row) => {
      if (!row) return '—'
      if (row.tipo === 'ore') {
        return formatDate(row.data_inizio)
      }
      if (row.data_inizio === row.data_fine) {
        return formatDate(row.data_inizio)
      }
      return `${formatDate(row.data_inizio)} → ${formatDate(row.data_fine)}`
    }

    const formatOreFascia = (row) => {
      if (!row || row.tipo !== 'ore') return ''
      const ore = formatOre(row.ore)
      const from = formatTime(row.ora_inizio)
      const to = formatTime(row.ora_fine)
      const fascia = from && to ? `${from}–${to}` : ''
      return [ore, fascia].filter(Boolean).join(' · ')
    }

    const toApiDate = (value) => {
      if (!value) return null
      const d = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(d.getTime())) return null
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    }

    const toApiTime = (value) => {
      const formatted = formatTime(value)
      return formatted || null
    }

    const tipoLabel = (tipo) => (tipo === 'ore' ? 'Ore' : 'Giorni')

    const tipoSeverity = (tipo) => (tipo === 'ore' ? 'info' : 'secondary')

    const statoLabel = (stato) => {
      const map = {
        in_attesa: 'In attesa',
        accettata: 'Accettata',
        respinta: 'Respinta',
        revocata: 'Revocata',
      }
      return map[stato] || stato
    }

    const statoSeverity = (stato) => {
      const map = {
        in_attesa: 'warning',
        accettata: 'success',
        respinta: 'danger',
        revocata: 'secondary',
      }
      return map[stato] || 'info'
    }

    const setFormTipo = (tipo) => {
      form.value = {
        ...form.value,
        tipo,
        data_fine: tipo === 'giorni' ? form.value.data_fine : null,
        ore: tipo === 'ore' ? form.value.ore : null,
        ora_inizio: tipo === 'ore' ? form.value.ora_inizio : null,
        ora_fine: tipo === 'ore' ? form.value.ora_fine : null,
      }
    }

    const onOreTimesChange = () => {
      const start = form.value.ora_inizio
      const end = form.value.ora_fine
      if (!(start instanceof Date) || !(end instanceof Date)) return
      const diff = (end.getTime() - start.getTime()) / 3600000
      if (diff <= 0) return
      form.value.ore = Math.round(diff * 2) / 2
    }

    const canRevoke = (row) => {
      if (!row || !currentUserId) return false
      if (Number(row.user_id) !== Number(currentUserId)) return false
      return row.stato === 'in_attesa'
    }

    const confirmRevoca = (row) => {
      if (!canRevoke(row)) return
      const tipo = row.tipo === 'ore' ? 'permesso' : 'ferie'
      const message = `Vuoi revocare questa richiesta di ${tipo} (${formatPeriodo(row)})?`

      if (confirm.require) {
        confirm.require({
          message,
          header: 'Revoca richiesta',
          icon: 'pi pi-exclamation-triangle',
          acceptClass: 'p-button-danger',
          acceptLabel: 'Si, revoca',
          rejectLabel: 'No',
          accept: () => {
            revocaRichiesta(row)
          },
        })
        return
      }

      if (window.confirm(message)) {
        revocaRichiesta(row)
      }
    }

    const revocaRichiesta = async (row) => {
      if (!row?.id) return
      revocandoId.value = row.id
      try {
        const res = await service.revocaRichiestaFerie({ id: row.id })
        if (res?.success === false) {
          throw new Error(res.message || 'Revoca non riuscita')
        }
        toast.add({
          severity: 'success',
          summary: 'Revocata',
          detail: res?.message || 'Richiesta revocata',
          life: 3000,
        })
        await loadRichieste()
      } catch (e) {
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: e?.response?.data?.message || e?.message || 'Revoca non riuscita',
          life: 4000,
        })
      } finally {
        revocandoId.value = null
      }
    }

    const loadRichieste = async () => {
      loading.value = true
      try {
        const params = {}
        if (isApprover.value && statoFilter.value) {
          params.stato = statoFilter.value
        }
        const res = await service.listRichiesteFerie(params)
        if (res?.success === false) {
          throw new Error(res.message || 'Errore caricamento richieste')
        }
        isApprover.value = !!res?.is_approver
        richieste.value = Array.isArray(res?.data) ? res.data : []
      } catch (e) {
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: e?.message || 'Impossibile caricare le richieste',
          life: 4000,
        })
      } finally {
        loading.value = false
      }
    }

    const setStatoFilter = async (value) => {
      statoFilter.value = value
      await loadRichieste()
    }

    const slugFilter = (label) => {
      return String(label)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
    }

    const exportExcel = () => {
      if (!isApprover.value) return
      if (!richieste.value.length) {
        toast.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: 'Nessuna richiesta da esportare per questo filtro',
          life: 3000,
        })
        return
      }

      const filtro = statoFilterLabel.value
      const rows = richieste.value.map((row) => ({
        Dipendente: row.richiedente || '',
        Email: row.richiedente_email || '',
        Tipo: tipoLabel(row.tipo),
        'Data inizio': formatDate(row.data_inizio),
        'Data fine': row.tipo === 'ore' ? formatDate(row.data_inizio) : formatDate(row.data_fine),
        Ore: row.tipo === 'ore' && row.ore != null ? Number(row.ore) : '',
        Dalle: row.tipo === 'ore' ? formatTime(row.ora_inizio) : '',
        Alle: row.tipo === 'ore' ? formatTime(row.ora_fine) : '',
        Motivazione: row.motivazione || '',
        Stato: statoLabel(row.stato),
        'Inviata il': formatDateTime(row.created_at),
        'Gestito da': row.gestito_da_nome || '',
        'Gestito il': formatDateTime(row.gestito_il),
        Note: row.note_gestione || '',
      }))

      const worksheet = XLSX.utils.json_to_sheet(rows)
      worksheet['!cols'] = [
        { wch: 22 },
        { wch: 28 },
        { wch: 10 },
        { wch: 14 },
        { wch: 14 },
        { wch: 8 },
        { wch: 8 },
        { wch: 8 },
        { wch: 40 },
        { wch: 12 },
        { wch: 18 },
        { wch: 22 },
        { wch: 18 },
        { wch: 30 },
      ]
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, filtro.slice(0, 31))
      const today = new Date().toISOString().slice(0, 10)
      XLSX.writeFile(workbook, `ferie_permessi_${slugFilter(filtro)}_${today}.xlsx`)
    }

    const openNewDialog = () => {
      form.value = emptyForm()
      showCreateDialog.value = true
    }

    const submitCreate = async () => {
      const tipo = form.value.tipo === 'ore' ? 'ore' : 'giorni'
      const dataInizio = toApiDate(form.value.data_inizio)
      const dataFine = tipo === 'ore' ? dataInizio : toApiDate(form.value.data_fine)
      const motivazione = (form.value.motivazione || '').trim()
      const ore = tipo === 'ore' ? Number(form.value.ore) : null
      const oraInizio = tipo === 'ore' ? toApiTime(form.value.ora_inizio) : null
      const oraFine = tipo === 'ore' ? toApiTime(form.value.ora_fine) : null

      if (tipo === 'giorni') {
        if (!dataInizio || !dataFine || motivazione.length < 3) {
          toast.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Compila date e motivazione (min. 3 caratteri)',
            life: 3500,
          })
          return
        }
      } else {
        if (!dataInizio || !Number.isFinite(ore) || ore < 0.5 || motivazione.length < 3) {
          toast.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Compila data, ore e motivazione (min. 3 caratteri)',
            life: 3500,
          })
          return
        }
        if ((oraInizio && !oraFine) || (!oraInizio && oraFine)) {
          toast.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Indica sia dalle che alle, oppure lascia entrambi vuoti',
            life: 3500,
          })
          return
        }
        if (oraInizio && oraFine && oraFine <= oraInizio) {
          toast.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'L\'ora di fine deve essere successiva all\'ora di inizio',
            life: 3500,
          })
          return
        }
      }

      saving.value = true
      try {
        const payload = {
          tipo,
          data_inizio: dataInizio,
          data_fine: dataFine,
          motivazione,
        }
        if (tipo === 'ore') {
          payload.ore = ore
          payload.ora_inizio = oraInizio
          payload.ora_fine = oraFine
        }
        const res = await service.createRichiestaFerie(payload)
        if (res?.success === false) {
          throw new Error(res.message || 'Invio non riuscito')
        }
        toast.add({
          severity: 'success',
          summary: 'Inviata',
          detail: tipo === 'ore' ? 'Richiesta ore inviata correttamente' : 'Richiesta ferie inviata correttamente',
          life: 3000,
        })
        showCreateDialog.value = false
        await loadRichieste()
      } catch (e) {
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: e?.response?.data?.message || e?.message || 'Invio non riuscito',
          life: 4000,
        })
      } finally {
        saving.value = false
      }
    }

    const openGestisci = (row, stato) => {
      selectedRichiesta.value = row
      gestisciStato.value = stato
      noteGestione.value = ''
      showGestisciDialog.value = true
    }

    const confirmGestisci = async () => {
      if (!selectedRichiesta.value) return
      gestendoId.value = selectedRichiesta.value.id
      try {
        const res = await service.gestisciRichiestaFerie({
          id: selectedRichiesta.value.id,
          stato: gestisciStato.value,
          note_gestione: noteGestione.value || null,
        })
        if (res?.success === false) {
          throw new Error(res.message || 'Operazione non riuscita')
        }
        toast.add({
          severity: 'success',
          summary: 'Ok',
          detail: res?.message || 'Richiesta aggiornata',
          life: 3000,
        })
        showGestisciDialog.value = false
        await loadRichieste()
      } catch (e) {
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: e?.response?.data?.message || e?.message || 'Operazione non riuscita',
          life: 4000,
        })
      } finally {
        gestendoId.value = null
      }
    }

    onMounted(async () => {
      self.$eventBus.emit('page-change', {
        target: self,
        currentRoute: router.currentRoute.value,
      })
      await loadRichieste()
    })

    return {
      loading,
      saving,
      richieste,
      isApprover,
      statoFilter,
      statoFilterOptions,
      statoFilterLabel,
      tipoOptions,
      showCreateDialog,
      showGestisciDialog,
      form,
      selectedRichiesta,
      gestisciStato,
      noteGestione,
      gestendoId,
      revocandoId,
      formatDate,
      formatDateTime,
      formatPeriodo,
      formatOreFascia,
      tipoLabel,
      tipoSeverity,
      statoLabel,
      statoSeverity,
      setFormTipo,
      onOreTimesChange,
      setStatoFilter,
      exportExcel,
      openNewDialog,
      submitCreate,
      openGestisci,
      confirmGestisci,
      canRevoke,
      confirmRevoca,
    }
  },
}
</script>

<style scoped>
.richiesta-ferie-page {
  min-height: 60vh;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.page-subtitle {
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.95rem;
}

.filters-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.export-btn {
  margin-left: auto;
}

.filter-label {
  font-weight: 600;
  color: #334155;
}

.filter-chips {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.filter-chip {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  border-radius: 999px;
  padding: 0.35rem 0.85rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.filter-chip.active {
  background: #0f4c81;
  border-color: #0f4c81;
  color: #fff;
}

.user-cell,
.periodo-cell {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.user-cell small,
.periodo-cell small {
  color: #64748b;
}

.motivazione-text {
  white-space: pre-wrap;
}

.azioni {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.azioni-employee {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
}

.gestione-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  color: #475569;
}

.note-gestione {
  font-style: italic;
}

.text-muted {
  color: #94a3b8;
}

.tipo-field {
  margin-bottom: 1rem;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.field-full {
  grid-column: 1 / -1;
}

.field label {
  font-weight: 600;
  color: #334155;
}

.gestisci-summary {
  margin: 0 0 1rem;
  color: #475569;
}

@media (max-width: 640px) {
  .page-header {
    flex-direction: column;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
